import {makeAutoObservable} from "mobx";
import {displayErrorDialogPrompt} from "../common/util/errror/log.util";
import {DATE_UTIL} from "../common/util/date/date.util";

export const State = {
    Authenticated: 'Authenticated',
    NotAuthenticated: 'NotAuthenticated',
    Pending: 'Pending',
    Failed: 'Failed',
};

export const LocalStorageTokenKey = '_AUTHENTICATION_TOKEN_';
export const LocalStorageSaveUserIdKey = '_AUTHENTICATION_SAVED_USER_ID_';
export const LocalStorageSaveUserIdExpireTimeKey = '_AUTHENTICATION_SAVED_USER_ID_EXPIRE_TIME_';

const EmptyLogin = {
    id: '',
    password: '',
};

const EmptyUser = {
    id: '',
    name: '',
    nickName: '',
    type: '',
    localeCode: '',
    localeGroupId : 0,
    changePassword: false,
    createdDatetime: '',
    updatedDatetime: '',
};

const logPrefix  = "[AuthStore]"
export default class AuthStore {
    constructor(props) {
        this.authRepository = props.authRepository;
        this.dialogStore = props.dialogStore;

        makeAutoObservable(this);
    }
    login = Object.assign({}, EmptyLogin);
    loginState = State.NotAuthenticated;
    loginToken = '';
    loginUser = Object.assign({}, EmptyUser);
    isSaveId = false;
    isFindPasswordOpen = false;
    isSendEmail = false;
    findUserId = '';
    newPassword = '';
    confirmNewPassword = '';
    isChangePasswordDialogOpen = false;

    openErrorDialog = (info) => {
        if (this?.dialogStore) {
            this.dialogStore.openErrorDialog(info)
        }
    };

    setLoginUser = (user) => {
        this.loginUser = user;
    }

    changeLoginId = (id) => {
        this.login.id = id;
    };

    changeLoginPassword = (password) => {
        this.login.password = password;
    };

    changeIsSaveId = (isSave) => {
        this.isSaveId = isSave;
    };

    changeSignInId = (id) => {
        this.login = { ...this.login, id: id };
    };

    setSavedId = () => {
        const expireDate = new Date();
        expireDate.setDate(new Date().getDate() + 7);

        localStorage.setItem(LocalStorageSaveUserIdKey, this.login.id);
        localStorage.setItem(LocalStorageSaveUserIdExpireTimeKey, expireDate.getTime().toString());
    }

    getSavedId = () => {
        return localStorage.getItem(LocalStorageSaveUserIdKey);
    }

    getSavedIdExpireTime = () => {
        return localStorage.getItem(LocalStorageSaveUserIdExpireTimeKey);
    }

    removeSavedId = () => {
        localStorage.removeItem(LocalStorageSaveUserIdKey);
        localStorage.removeItem(LocalStorageSaveUserIdExpireTimeKey);
    }

    setIsFindPasswordOpen = (open) => {
        this.isFindPasswordOpen = open;
    }

    setFindUserId = (userId) => {
        this.findUserId = userId;
    }

    setNewPassword = (password) => {
        this.newPassword = password;
    }

    setConfirmNewPassword = (password) => {
        this.confirmNewPassword = password;
    }

    invalidateLogin = () => {
        this.login = Object.assign({}, EmptyLogin);
        this.loginState = State.NotAuthenticated;
        this.loginToken = '';
        this.loginUser = Object.assign({}, EmptyUser);
    };

    getUserType = () => {
        if (this.loginUser) {
            return this.loginUser.type;
        } else {
            return 'None';
        }
    }

    setIsChangePasswordDialogOpen = (open) => {
        this.isChangePasswordDialogOpen = open;
    }

    * doLogin(callback) {
        this.loginState = State.Pending;

        try {
            console.log(logPrefix, 'Start doLogin.');

            const url = `signin`;
            const param = this.login;
            const response = yield this.authRepository.login(url, param);

            const token = response.data.token;
            const user = response.data.user;

            localStorage.setItem(LocalStorageTokenKey, token);

            console.log('doLogin');
            console.log(this);

            this.loginState = State.Authenticated;
            this.loginToken = token;
            this.loginUser = user;

            DATE_UTIL.setTimeZone(DATE_UTIL.getLocalTimeZone());

            if (this.isSaveId) {
                this.setSavedId();
            }

            console.log(logPrefix, 'Finished doLogin.');
            callback && callback();
        } catch (e) {
            console.log(logPrefix, 'Failed doLogin.');
            this.loginState = State.Failed;
            this.loginToken = '';
            this.loginUser = Object.assign({}, EmptyUser);
        }
    }

    * checkLogin() {
        const token = localStorage.getItem(LocalStorageTokenKey);

        if(token) {
            try {
                const url = `signcheck`;
                const response = yield this.authRepository.checkLogin(url);

                const user = response.data;
                this.loginState = State.Authenticated;
                this.loginUser = user;

                DATE_UTIL.setTimeZone(DATE_UTIL.getLocalTimeZone());
            } catch(e) {
                this.loginState = State.NotAuthenticated;
                this.loginToken = '';
                this.loginUser = Object.assign({}, EmptyUser);
            }
        }
    }

    * doLogout(callback) {
        try {
            const url = `signout`;
            yield this.authRepository.logout(url);

            console.log(this);
            this.login = Object.assign({}, EmptyLogin);
            this.loginState = State.NotAuthenticated;
            this.loginToken = '';
            this.loginUser = Object.assign({}, EmptyUser);

            callback && callback();
        } catch(e) {
            this.login = Object.assign({}, EmptyLogin);
            this.loginState = State.NotAuthenticated;
            this.loginToken = '';
            this.loginUser = Object.assign({}, EmptyUser);
        } finally {
            localStorage.removeItem(LocalStorageTokenKey);
        }
    }

    * sendFindPasswordEmail(intl) {
        try {
            const url = `/${this.findUserId}/password/find`
            yield this.authRepository.sendFindPasswordEmail(url);

            this.isFindPasswordOpen = false;
            this.isSendEmail = true;
            setTimeout(() => {
                this.isSendEmail = false;
            }, 3000);
        } catch(e) {
            console.log(logPrefix, 'Failed sendFindPasswordEmail.');
            const errorCode = e.response.data.errorCode;
            let errorText = '메일 전송에 실패하였습니다.';

            if (errorCode === 'CanNotFoundUser' || errorCode === 'CanNotFoundEmail') {
                errorText = intl.formatMessage({id: errorCode});
            }
            this.openErrorDialog({
                titleText: '이메일 전송 오류',
                text: errorText
            });
        }
    }

    * resetPassword(id, authToken, callback) {
        try {
            const url = `/${id}/password/reset`
            const param = {id: id, password: this.newPassword, authToken: authToken};
            yield this.authRepository.resetPassword(url, param);

            callback && callback();
        } catch(e) {
            console.log(logPrefix, 'Failed sendFindPasswordEmail.');
            this.openErrorDialog({
                titleText: '비밀번호 변경 오류',
                text: displayErrorDialogPrompt(e, '비밀번호를 변경할 수 없습니다. 관리자에게 문의해주세요.')
            });
        }
    }
}
